class PasMainProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    try {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }

    this.variantInput = this.querySelector('[data-product-variant-id]');
    this.activeVariant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value) || this.variants[0] || null;
    this.gallery = this.querySelector('[data-product-gallery]');
    this.galleryItems = Array.from(this.querySelectorAll('[data-gallery-item]'));
    this.galleryThumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.activeMediaIndex = Math.max(0, this.galleryItems.findIndex((item) => item.classList.contains('is-active')));
    this.stage = this.querySelector('[data-gallery-stage]');
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.lastFocused = null;
    this.touchStartX = null;

    this.addEventListener('click', (event) => this.handleClick(event));
    this.querySelector('[data-quantity-input]')?.addEventListener('change', (event) => this.normalizeQuantity(event.currentTarget));
    this.stage?.addEventListener('touchstart', (event) => {
      this.touchStartX = event.touches[0]?.clientX ?? null;
    }, { passive: true });
    this.stage?.addEventListener('touchend', (event) => this.handleSwipe(event), { passive: true });
    this.stage?.addEventListener('mousemove', (event) => this.updateZoomOrigin(event));
    this.dialog?.addEventListener('keydown', (event) => this.handleDialogKeydown(event));
    this.dialog?.addEventListener('close', () => this.restoreDialogFocus());
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.querySelector('.pas-product__form')?.addEventListener('submit', (event) => {
      if (!this.activeVariant?.available) event.preventDefault();
    });

    this.refreshVariantControls();
    if (this.galleryItems.length) this.selectMediaByIndex(this.activeMediaIndex, false);
    this.setupMobileSticky();
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const option = target.closest('[data-option-control]');
    if (option && this.contains(option) && !option.disabled) {
      const candidate = this.variantFor(Number(option.dataset.optionIndex), option.dataset.optionValue);
      if (candidate) this.selectVariant(candidate);
      return;
    }

    if (target.closest('[data-quantity-minus]')) {
      const input = this.querySelector('[data-quantity-input]');
      if (input) { input.value = String(Math.max(1, Number.parseInt(input.value, 10) - 1 || 1)); input.dispatchEvent(new Event('change', { bubbles: true })); }
      return;
    }
    if (target.closest('[data-quantity-plus]')) {
      const input = this.querySelector('[data-quantity-input]');
      if (input) { input.value = String(Math.max(1, Number.parseInt(input.value, 10) + 1 || 1)); input.dispatchEvent(new Event('change', { bubbles: true })); }
      return;
    }

    const thumb = target.closest('[data-gallery-thumb]');
    if (thumb) { this.selectMediaById(thumb.dataset.mediaId); return; }
    if (target.closest('[data-gallery-prev], [data-dialog-prev]')) { this.moveMedia(-1); return; }
    if (target.closest('[data-gallery-next], [data-dialog-next]')) { this.moveMedia(1); return; }
    if (target.closest('[data-gallery-zoom]')) { this.toggleZoom(); return; }
    if (target.closest('[data-gallery-expand]')) { this.openDialog(); return; }
    if (target.closest('[data-gallery-close]')) this.dialog?.close();
  }

  normalizeQuantity(input) {
    const value = Number.parseInt(input.value, 10);
    input.value = String(Number.isFinite(value) ? Math.max(1, value) : 1);
  }

  variantFor(optionIndex, value) {
    return window.PasVariantState?.resolve(this.variants, this.activeVariant, optionIndex, value) || null;
  }

  selectVariant(variant) {
    this.activeVariant = variant;
    if (this.variantInput) this.variantInput.value = variant.id;

    this.querySelectorAll('[data-product-price], [data-mobile-price]').forEach((node) => { node.textContent = variant.price; });
    const sku = this.querySelector('[data-product-sku]');
    if (sku) sku.textContent = variant.sku || '—';

    const availability = this.querySelector('[data-product-availability]');
    if (availability) {
      availability.textContent = variant.available ? (this.dataset.inStockText || 'In stock') : 'Sold out';
      availability.classList.toggle('is-available', variant.available);
    }

    const mainAdd = this.querySelector('[data-product-add]');
    const mobileAdd = this.querySelector('[data-mobile-add]');
    if (mainAdd) { mainAdd.disabled = !variant.available; mainAdd.textContent = variant.available ? `Add to cart — ${variant.price}` : 'Sold out'; }
    if (mobileAdd) { mobileAdd.disabled = !variant.available; mobileAdd.textContent = variant.available ? `Add to cart — ${variant.price}` : 'Sold out'; }

    const mobileVariant = this.querySelector('[data-mobile-variant]');
    if (mobileVariant) mobileVariant.textContent = variant.title;
    const mobileImage = this.querySelector('.pas-product__mobile-image');
    if (mobileImage && variant.image) { mobileImage.removeAttribute('srcset'); mobileImage.src = variant.image; }

    this.refreshVariantControls();
    if (variant.featuredMediaId) this.selectMediaById(variant.featuredMediaId);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    this.dispatchEvent(new CustomEvent('pas:variant-change', { bubbles: true, detail: { variant } }));
  }

  refreshVariantControls() {
    this.querySelectorAll('[data-option-control]').forEach((control) => {
      const index = Number(control.dataset.optionIndex);
      const value = control.dataset.optionValue;
      const candidate = this.variantFor(index, value);
      const active = this.activeVariant?.options[index] === value;
      const unavailable = !candidate || !candidate.available;
      control.disabled = unavailable && !active;
      control.classList.toggle('is-active', active);
      control.classList.toggle('is-unavailable', unavailable);
      control.setAttribute('aria-checked', String(active));
      control.setAttribute('aria-disabled', String(unavailable && !active));
      const optionName = control.closest('[role="radiogroup"]')?.getAttribute('aria-label') || 'Option';
      control.setAttribute('aria-label', `${optionName}: ${value}${unavailable ? candidate ? ' (sold out)' : ' (unavailable combination)' : ''}`);
      const optionPrice = control.querySelector('[data-option-price]');
      if (optionPrice) optionPrice.textContent = candidate?.price || '';
    });

    this.querySelectorAll('[data-option-selected]').forEach((label) => {
      const index = Number(label.dataset.optionSelected);
      label.textContent = this.activeVariant?.options[index] || '';
    });
  }

  selectMediaById(mediaId) {
    const index = this.galleryItems.findIndex((item) => String(item.dataset.mediaId) === String(mediaId));
    if (index >= 0) this.selectMediaByIndex(index);
  }

  selectMediaByIndex(index, focusThumb = false) {
    if (!this.galleryItems.length) return;
    const next = (index + this.galleryItems.length) % this.galleryItems.length;
    this.galleryItems.forEach((item, itemIndex) => {
      const active = itemIndex === next;
      if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
      item.querySelectorAll('video, iframe, model-viewer').forEach((media) => { media.tabIndex = active ? 0 : -1; });
    });
    this.galleryThumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === next;
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true'); else thumb.removeAttribute('aria-current');
    });
    this.activeMediaIndex = next;
    const count = this.querySelector('[data-gallery-count]');
    if (count) count.textContent = String(next + 1);
    this.stage?.classList.remove('is-zoomed');
    this.syncZoomButton();
    if (focusThumb) this.galleryThumbs[next]?.focus();
    if (this.dialog?.open) this.renderDialogMedia();
  }

  moveMedia(direction) {
    this.selectMediaByIndex(this.activeMediaIndex + direction);
  }

  handleSwipe(event) {
    const start = this.touchStartX;
    const end = event.changedTouches[0]?.clientX ?? null;
    this.touchStartX = null;
    if (start === null || end === null || Math.abs(end - start) < 48) return;
    this.moveMedia(end < start ? 1 : -1);
  }

  toggleZoom() {
    const button = this.querySelector('[data-gallery-zoom]');
    const zoomed = !this.stage?.classList.contains('is-zoomed');
    this.stage?.classList.toggle('is-zoomed', zoomed);
    if (button) { button.setAttribute('aria-pressed', String(zoomed)); button.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in'); }
  }

  updateZoomOrigin(event) {
    if (!this.stage?.classList.contains('is-zoomed')) return;
    const image = this.galleryItems[this.activeMediaIndex]?.querySelector('.pas-product-gallery__media--image');
    if (!image) return;
    const bounds = this.stage.getBoundingClientRect();
    image.style.transformOrigin = `${((event.clientX - bounds.left) / bounds.width) * 100}% ${((event.clientY - bounds.top) / bounds.height) * 100}%`;
  }

  syncZoomButton() {
    const button = this.querySelector('[data-gallery-zoom]');
    const isImage = this.galleryItems[this.activeMediaIndex]?.dataset.mediaType === 'image';
    if (!button) return;
    button.hidden = !isImage;
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', 'Zoom in');
  }

  openDialog() {
    if (!this.dialog) return;
    this.lastFocused = document.activeElement;
    this.renderDialogMedia();
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
    requestAnimationFrame(() => this.dialog.querySelector('[data-gallery-close]')?.focus());
  }

  renderDialogMedia() {
    const target = this.querySelector('[data-dialog-media]');
    const caption = this.querySelector('[data-dialog-caption]');
    const source = this.galleryItems[this.activeMediaIndex]?.querySelector('.pas-product-gallery__media');
    if (!target || !source) return;
    target.replaceChildren(source.cloneNode(true));
    const description = this.galleryItems[this.activeMediaIndex]?.dataset.mediaAlt || source.getAttribute('alt') || '';
    if (caption) caption.textContent = description;
  }

  handleDialogKeydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.dialog?.close(); return; }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.moveMedia(-1); return; }
    if (event.key === 'ArrowRight') { event.preventDefault(); this.moveMedia(1); return; }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(this.dialog.querySelectorAll('button:not(:disabled), video[controls], iframe, [tabindex]:not([tabindex="-1"])'));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  restoreDialogFocus() {
    this.querySelector('[data-dialog-media]')?.replaceChildren();
    if (this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
  }

  setupMobileSticky() {
    const buyBlock = this.querySelector('[data-buy-block]');
    const sticky = this.querySelector('[data-mobile-sticky]');
    if (!buyBlock || !sticky || typeof IntersectionObserver === 'undefined') return;
    this.buyObserver = new IntersectionObserver(([entry]) => {
      const visible = !(entry?.isIntersecting ?? true);
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      const addButton = sticky.querySelector('[data-mobile-add]');
      if (addButton) addButton.tabIndex = visible ? 0 : -1;
    }, { threshold: 0 });
    this.buyObserver.observe(buyBlock);
  }

  disconnectedCallback() {
    this.buyObserver?.disconnect();
  }
}

if (!customElements.get('pas-main-product')) customElements.define('pas-main-product', PasMainProduct);
